/**
 * ChatStreamHandler – AG-UI protocol stream handler
 *
 * Uses @ag-ui/client runHttpRequest + transformHttpEventStream to consume
 * an AG-UI SSE stream from the Lambda backend.
 *
 * All stream events mutate the chatStore directly instead of calling callbacks.
 * The store's signals drive Preact component re-renders automatically.
 *
 * AG-UI event → store mutation mapping:
 *   RUN_STARTED               → store.runStarted()
 *   RUN_FINISHED              → store.runFinished(thread_id) → onDone(id)
 *   TEXT_MESSAGE_CONTENT      → store.appendTextDelta(delta)
 *   TEXT_MESSAGE_END          → store.messageStop()
 *   REASONING_START           → store.reasoningStart()
 *   REASONING_MESSAGE_CONTENT → store.appendReasoningDelta(delta)
 *   TOOL_CALL_START           → store.setStatus('tool_{name}')
 *   SUBAGENT_STARTED          → store.agentRunning(name, subagentRunId) [ai/framework]
 *   TEXT_MESSAGE_* (w/ subagentRunId) → store.subagentDelta(...) [ai/framework]
 *   SUBAGENT_FINISHED         → store.subagentDone(agent, subagentRunId) [ai/framework]
 *   CUSTOM (agent_running)    → store.agentRunning(name, instanceId) [legacy hostagent]
 *   CUSTOM (subagent_tool_*)  → store.subagentTool{Start|End}(agent, tool, instanceId) [legacy hostagent]
 *   CUSTOM (subagent_delta)   → store.subagentDelta(agent, text, instanceId) [legacy hostagent]
 *   CUSTOM (subagent_done)    → store.subagentDone(agent, instanceId) [legacy hostagent]
 *   CUSTOM (stream_keepalive) → reset idle timer; flip to "still working"
 *   CUSTOM (stream_idle)      → synthetic local event from idle detector below
 *   CUSTOM (conversation_title) → store.setTitle(title)
 *   CUSTOM (cost_info)        → store.setCostInfo(info)
 *   CUSTOM (guardrail_blocked)→ _errorFired + onGuardrailBlocked() + runErrorFinish()
 *   CUSTOM (conversation_summarizing) → store.setStatus('conversation_summarizing')
 *   RUN_ERROR                 → onError(message)
 */

import { EventType, runHttpRequest, transformHttpEventStream } from '@ag-ui/client'
import { parseQuotaResponse, parseQuotaError } from '../../components/shared/quota/parseQuotaError.js'

const DEV_LOGGING = __DEV_LOGGING__

export class ChatStreamHandler {
  constructor(options = {}) {
    this._subscription = null
    this._threadId     = null
    this._doneFired    = false
    this._errorFired   = false
    this._idleTimer    = null
    this._lastEventAt  = 0

    // store is the chatStore — all UI state goes through it
    this.store = options.store || null

    // Minimal callbacks for controller-level concerns only
    // (URL update, budget exceeded, guardrail, error — not UI rendering)
    this.onDone             = options.onDone             || (() => {})
    this.onError            = options.onError            || (() => {})
    this.onGuardrailBlocked = options.onGuardrailBlocked || (() => {})
    this.onQuotaExceeded     = options.onQuotaExceeded     || (() => {})
    // Optional: async () => headers. Called once after a 401 (expired caller
    // token on aichat-service) to retry the request with fresh credentials.
    this.refreshAuthHeaders  = options.refreshAuthHeaders  || null
  }

  /**
   * Start an AG-UI stream.
   *
   * extraBody: optional extra fields merged into the request body (e.g. the
   * support Beacon widget sends { source: 'beacon' } so the backend can tag
   * the resulting Ai::Conversation and exclude it from the full /ai history).
   *
   * The caller identity is never sent in the body: aichat-service takes it from
   * the Bearer stream token, Rails endpoints (kb_stream) from the session.
   */
  async startStream(streamUrl, message, attachments = [], blobSignedIds = [], streamHeaders = {}, extraBody = {}) {
    this.abort()

    this._threadId  = null
    this._doneFired = false

    const body = {
      message,
      conversation_id: streamHeaders['X-Conversation-Id'] || null,
      ...extraBody,
    }
    if (blobSignedIds?.length > 0) body.blob_signed_ids = blobSignedIds
    else if (attachments?.length > 0) body.attachments = attachments

    const requestHeaders = {
      'Content-Type':  'application/json',
      'Accept':        'text/event-stream',
      ...streamHeaders,
    }
    delete requestHeaders['X-Conversation-Id']

    if (DEV_LOGGING) console.log('[ChatStreamHandler] Starting AG-UI stream', { streamUrl, body })

    const doFetch = (headers) => fetch(streamUrl, {
      method: 'POST',
      headers,
      body: JSON.stringify(body),
      credentials: 'include',
    })

    let res = await doFetch(requestHeaders)
    if (res.status === 401 && this.refreshAuthHeaders) {
      const fresh = await this.refreshAuthHeaders().catch(() => null)
      if (fresh?.Authorization) res = await doFetch({ ...requestHeaders, ...fresh })
    }

    const quota = await parseQuotaResponse(res)
    if (quota) {
      this._stopIdleTimer()
      this.onQuotaExceeded(quota)
      return
    }

    if (!res.ok) {
      let errMsg = `HTTP ${res.status}`
      try {
        const errBody = await res.clone().json()
        if (errBody?.message === 'budget_exceeded') {
          const legacy = parseQuotaError({ error: 'quota_exceeded', code: 'quota_exceeded', metric: 'aichat.input_tokens', barrier_mode: 'hard_stop', used: 0, limit: 0, remaining: 0, upgrades: [] }, 402)
          if (legacy) {
            this.onQuotaExceeded(legacy)
            return
          }
        }
        errMsg = errBody?.message || errMsg
      } catch { /* ignore */ }
      this.onError(errMsg)
      return
    }

    const http$   = runHttpRequest(() => Promise.resolve(res))
    const events$ = transformHttpEventStream(http$)

    this._subscription = events$.subscribe({
      next:     (event) => this._handleEvent(event),
      error:    (err) => {
        if (DEV_LOGGING) console.error('[ChatStreamHandler] stream error:', err)
        this._stopIdleTimer()
        if (err?.name !== 'AbortError') this.onError(err?.message || 'Stream error')
      },
      complete: () => {
        if (DEV_LOGGING) console.log('[ChatStreamHandler] stream complete')
        this._stopIdleTimer()
        if (!this._doneFired) {
          this._doneFired = true
          const id = this.store?.runFinished(this._threadId)
          this.onDone(this._threadId, id)
          this._threadId = null
        }
      },
    })

    this._startIdleTimer()
  }

  // ── Idle detector ─────────────────────────────────────────────────────────
  // If no SSE event arrives for IDLE_THRESHOLD_MS, flip the status to
  // "still working" so the user sees the UI is alive. The backend also emits
  // CUSTOM stream_keepalive every ~25s while a tool runs (handler.py); this
  // is a belt-and-braces fallback for network stalls or ALB buffering.

  _startIdleTimer() {
    this._stopIdleTimer()
    this._lastEventAt = Date.now()
    const IDLE_THRESHOLD_MS = 20_000
    this._idleTimer = setInterval(() => {
      if (Date.now() - this._lastEventAt >= IDLE_THRESHOLD_MS) {
        if (DEV_LOGGING) console.log('[ChatStreamHandler] stream_idle (no events for', Date.now() - this._lastEventAt, 'ms)')
        if (this.store && !this._doneFired) {
          this.store.setStatus('still_working', null)
        }
      }
    }, IDLE_THRESHOLD_MS / 2)
  }

  _stopIdleTimer() {
    if (this._idleTimer) {
      clearInterval(this._idleTimer)
      this._idleTimer = null
    }
  }

  // ── Event handler ─────────────────────────────────────────────────────────

  _handleEvent(event) {
    if (DEV_LOGGING) console.log('[ChatStreamHandler] event:', event.type, event)
    this._lastEventAt = Date.now()

    switch (event.type) {
      // ── Lifecycle ────────────────────────────────────────────────────
      case EventType.RUN_STARTED: {
        this._threadId = event.threadId || event.thread_id || null
        this.store?.runStarted()
        break
      }

      case EventType.RUN_FINISHED: {
        if (this._doneFired || this._errorFired) break
        this._doneFired = true
        const threadId = this._threadId
        this._threadId = null
        const msgId = this.store?.runFinished(threadId)
        this.onDone(threadId, msgId)
        break
      }

      // ── Text streaming ───────────────────────────────────────────────
      case EventType.TEXT_MESSAGE_START:
        // ai/framework attributes a subagent's own text chunk via
        // `subagentRunId` (standard AG-UI ``Attributable`` mixin) instead of
        // a dedicated CUSTOM event — route it to the SubagentPanel instead
        // of touching the main AiBubble/ActivityIndicator state.
        if (event.subagentRunId) break
        // Clear tool status now that text is arriving; ActivityIndicator shows default label
        this.store?.clearStatus()
        break

      case EventType.TEXT_MESSAGE_CONTENT: {
        const delta = event.delta || ''
        if (!delta) break
        if (event.subagentRunId) {
          this.store?.subagentDelta('', delta, event.subagentRunId)
        } else {
          this.store?.appendTextDelta(delta)
        }
        break
      }

      case EventType.TEXT_MESSAGE_END:
        if (event.subagentRunId) break
        this.store?.messageStop()
        break

      // ── Reasoning ────────────────────────────────────────────────────
      case EventType.REASONING_START:
        this.store?.reasoningStart()
        break

      case EventType.REASONING_MESSAGE_CONTENT: {
        const delta = event.delta || ''
        if (delta) this.store?.appendReasoningDelta(delta)
        break
      }

      case EventType.REASONING_MESSAGE_START:
      case EventType.REASONING_MESSAGE_END:
        break

      case EventType.REASONING_END:
        break

      // ── Subagents (ai/framework native events) ────────────────────────
      case EventType.SUBAGENT_STARTED: {
        if (event.name) {
          this.store?.agentRunning(event.name, event.subagentRunId || null)
          this.store?.clearStatus()  // SubagentPanel takes over — clear ActivityIndicator
        }
        break
      }

      case EventType.SUBAGENT_FINISHED:
        this.store?.subagentDone('', event.subagentRunId || null)
        break

      // ── Tool calls ───────────────────────────────────────────────────
      case EventType.TOOL_CALL_START: {
        // Set status so ActivityIndicator shows the tool name for direct MCP calls.
        // If this is a subagent call, agent_running/SUBAGENT_STARTED fires
        // shortly after and clears the status — SubagentPanel takes over
        // from there.
        const rawName  = event.toolCallName || 'unknown'
        const toolName = rawName.includes('___') ? rawName.split('___').pop() : rawName
        this.store?.setStatus(`tool_${toolName}`, null)
        break
      }

      case EventType.STEP_STARTED:
      case EventType.STEP_FINISHED:
      case EventType.TOOL_CALL_ARGS:
      case EventType.TOOL_CALL_END:
        break

      // ── State / metadata ─────────────────────────────────────────────
      case EventType.STATE_SNAPSHOT:
        if (DEV_LOGGING) console.log('[ChatStreamHandler] metadata snapshot:', event.snapshot)
        break

      // ── Custom app events ─────────────────────────────────────────────
      case EventType.CUSTOM: {
        if (event.name === 'conversation_summarizing') {
          this.store?.setStatus('conversation_summarizing', null)
        } else if (event.name === 'guardrail_blocked') {
          // Treat like RUN_ERROR: hostagent still emits RUN_FINISHED after a
          // guardrail stop. Without _errorFired, runFinished() would clear the
          // error StatusAlert and leave an empty turn.
          if (this._errorFired) break
          this._errorFired = true
          this.onGuardrailBlocked()   // appendTextDelta if empty + setStatus
          this.store?.runErrorFinish() // finalize bubble; keep status
        } else if (event.name === 'cost_info') {
          this.store?.setCostInfo(event.value || {})
        } else if (event.name === 'agent_running') {
          const val = event.value || {}
          const name = val.name || null
          if (name) {
            this.store?.agentRunning(name, val.instanceId || null)
            this.store?.clearStatus()  // SubagentPanel takes over — clear ActivityIndicator
          }
        } else if (event.name === 'subagent_tool_start') {
          const val = event.value || {}
          if (val.agent && val.tool) this.store?.subagentToolStart(val.agent, val.tool, val.instanceId || null)
        } else if (event.name === 'subagent_tool_end') {
          const val = event.value || {}
          if (val.agent) this.store?.subagentToolEnd(val.agent, val.tool || '', val.instanceId || null)
        } else if (event.name === 'subagent_delta') {
          const val = event.value || {}
          if (val.agent && val.text) this.store?.subagentDelta(val.agent, val.text, val.instanceId || null)
        } else if (event.name === 'subagent_done') {
          const val = event.value || {}
          if (val.agent || val.instanceId) this.store?.subagentDone(val.agent || '', val.instanceId || null)
        } else if (event.name === 'stream_keepalive') {
          // Backend heartbeat (every 25s while ANY tool is in flight) — already
          // bumped _lastEventAt above; if no other status is set, hint that the
          // run is still alive but slow.
          if (DEV_LOGGING) console.log('[ChatStreamHandler] stream_keepalive', event.value)
        } else if (event.name === 'conversation_title') {
          const title = event.value?.title
          if (title) this.store?.setTitle(title)
        } else if (DEV_LOGGING) {
          console.log('[ChatStreamHandler] unhandled CUSTOM event:', event.name, event.value)
        }
        break
      }

      // ── Errors ───────────────────────────────────────────────────────
      case EventType.RUN_ERROR: {
        const msg = event.message || 'Unknown error'
        if (msg.includes('insufficient messages') || msg.includes('Cannot summarize')) break
        if (DEV_LOGGING) console.error('[ChatStreamHandler] RUN_ERROR:', msg)
        this._errorFired = true
        this.store?.runErrorFinish()
        this.onError(msg)
        this.abort()
        break
      }

      default:
        if (DEV_LOGGING) console.log('[ChatStreamHandler] unhandled event type:', event.type)
    }
  }

  // ── Public API ────────────────────────────────────────────────────────────

  abort() {
    this._stopIdleTimer()
    if (this._subscription) {
      try { this._subscription.unsubscribe() } catch { /* ignore */ }
      this._subscription = null
    }
  }
}
